import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {DefaultPaginationInfo} from "../../../../../../core/constants/enums";
import IconButton from "../../../../../components/base/button/icon_button";
import {ArrowBack, ReceiptLongRounded} from "@mui/icons-material";
import {Row} from "reactstrap";
import Api from "../../../../../../core/services/api/api";
import useIsMounted from "../../../../../hooks/use-is-mounted";
import {formatMoney} from "../../../../../../core/services/utils/utils";
import CreditCard from "../../../../../components/app-specific/payment-cards/credit-card";
import CreditCardTransactionSummarySearchSection from "../../../../../components/search-sections/credit-card-transaction-summary";
import {CreditCardTransactionsSummaryDataGridCellKeys} from "../../../../../../core/constants/table/data-grid-column-keys";
import {DataGridColumnAlignments, DataGridColumnTypes, useDataGridSavedState} from "../../../../../../packages/data-grid";
import {ReactComponent as Passed} from "../../../../../../assets/images/validations/passed.svg";
import {ReactComponent as Failed} from '../../../../../../assets/images/validations/failed.svg';
import {ReactComponent as RollBackIdentifier} from '../../../../../../assets/images/transaction-identifier-icon.svg';
import PartnerDataGrid from "../../../../../containers/data-grid";
import TransactionReceiptDialog from "../../../../../components/dialogs/transaction-receipt-dialog";
import useSearch from "../../../../../hooks/use-search";
import {useConfigureNotebook} from "../../../../../hooks/use-notebook-config";
import DateUtils from "../../../../../../core/services/date-utils";
import {useHistory, useParams} from "react-router-dom";
import {useAppSelector} from "../../../../../redux";
import useAccessLevels from "../../../../../hooks/use-access-levels";
import useBehavioralAnalyticsTool from "../../../../../hooks/use-behavioural-analytics-tool";


const orderByPropNames = [
    CreditCardTransactionsSummaryDataGridCellKeys.succeeded,
    CreditCardTransactionsSummaryDataGridCellKeys.identifier,
    CreditCardTransactionsSummaryDataGridCellKeys.type,
    CreditCardTransactionsSummaryDataGridCellKeys.amount,
    CreditCardTransactionsSummaryDataGridCellKeys.submitDateTime,
    CreditCardTransactionsSummaryDataGridCellKeys.iso,
    CreditCardTransactionsSummaryDataGridCellKeys.responseCode,
];

const initReceiptDialogState = {title: '', open: false, customerId: undefined, receiptId: undefined, close: () => void 0};

const CustomerCreditCardSummaryView = () => {
    const params = useParams();
    const history = useHistory();
    const [data, setData] = useState([]);
    const [cardLoading, setCardLoading] = useState(false);
    const [cardInfo, setCardInfo] = useState();
    const [receiptDialogState, setReceiptDialogState] = useState(initReceiptDialogState);
    const isMounted = useIsMounted();
    const defaultCustomer = useAppSelector(state => state.customerInformation);
    /*** @type {React.MutableRefObject<DataGridController>}*/
    const dataGridRef = useRef();
    const operatorAccessLevels = useAccessLevels();

    const showCard = useMemo(() => !!Object.keys(cardInfo ?? {})?.length && !cardLoading, [cardInfo, cardLoading]);

    const {configureNotebookTags} = useConfigureNotebook('customer-credit-card-summary-view');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();

    /**
     * As soon as the component is mounted:
     * - Configures the notebook tags for the view
     * - Configures the behavioral analytics tool for the view
     */
    useEffect(() => {
        if (!defaultCustomer?.code)
            return;
        configureNotebookTags((noteTags) => noteTags.customerCardSummaryCreditCards(defaultCustomer.code, params.id));
        configureBehavioralTool((tags) => tags.customerCardSummaryCreditCards(defaultCustomer.code, params.id));
    }, [configureBehavioralTool, configureNotebookTags, defaultCustomer.code, params.id]);

    /**
     * Fetches the card information from the server with the provided id in the url params.
     *
     * if the result of the api is successful, sets the internal state
     * @return {Promise<void>}
     */
    const getCardInformation = useCallback(async () => {
        setCardLoading(true);
        const response = await Api.getCustomerCreditCardsV2(params.customerId);
        if (!isMounted())
            return;
        setCardLoading(false);
        if (response?.resultFlag) {
            setCardInfo(response.data?.find(e => e.id === Number(params.id)));
        }
    }, [isMounted, params.customerId, params.id])

    /**
     * Fetches the card information from the server with the provided id in the url params.
     */
    useEffect(() => {
        void getCardInformation();
    }, [getCardInformation])

    const dataGridSaveConfig = useMemo(() => ({
        name: 'PaymentBasedUserCreditCardSummaryListViewDataGrid',
        version: 2,
    }), [])

    /**
     * Memo version of data grid column entities
     * @type {DataGridColumn[]}
     */
    const columns = useMemo(() =>
        /** @type {DataGridColumn[]}*/
        [
            ...(operatorAccessLevels.packages.sales.paymentReceipts ? [
                {
                    name: CreditCardTransactionsSummaryDataGridCellKeys.receipt,
                    alignment: DataGridColumnAlignments.center,
                    type: DataGridColumnTypes.element,
                    width: 0,
                    resizable: false,
                    visibilityToggleable: false,
                    pinnedToggleable: false,
                    reorderable: false,
                    sortable: false,
                    pinned: true,
                },
            ] : []),
            {
                title: "Succeeded",
                name: CreditCardTransactionsSummaryDataGridCellKeys.succeeded,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.element,
                pinned: true,
                sortable: true,
                pinnedToggleable: true,
                resizable: false,
                visibilityToggleable: true,
                width: 90,
            },
            {
                title: "Identifier",
                name: CreditCardTransactionsSummaryDataGridCellKeys.identifier,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element
            },
            {
                title: "Original Payment",
                name: CreditCardTransactionsSummaryDataGridCellKeys.originalPayment,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element,
                sortable: false,
                width: 150,
            },
            {
                title: 'Type',
                name: CreditCardTransactionsSummaryDataGridCellKeys.type,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
            },
            {
                title: 'Amount',
                name: CreditCardTransactionsSummaryDataGridCellKeys.amount,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.money,
            },
            {
                title: 'Auth Code',
                name: CreditCardTransactionsSummaryDataGridCellKeys.authCode,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.string,
                sortable: false,
            },
            {
                title: 'Submit Date & Time',
                name: CreditCardTransactionsSummaryDataGridCellKeys.submitDateTime,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                width: 150,
            },
            {
                title: 'Provider Submit Date & Time',
                name: CreditCardTransactionsSummaryDataGridCellKeys.providerSubmitDateTime,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                width: 210,
            },
            {
                title: 'Message',
                name: CreditCardTransactionsSummaryDataGridCellKeys.message,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
            },
            {
                title: 'Payment Provider Invoice',
                name: CreditCardTransactionsSummaryDataGridCellKeys.monerisInvoice,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
                width: 300,
            },
            {
                title: 'Reference',
                name: CreditCardTransactionsSummaryDataGridCellKeys.reference,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.number,
                sortable: false,
            },
            {
                title: 'ISO',
                name: CreditCardTransactionsSummaryDataGridCellKeys.iso,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.string,
            },
            {
                title: 'Response Code',
                name: CreditCardTransactionsSummaryDataGridCellKeys.responseCode,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.string,
                width: 150,
            },
        ], [operatorAccessLevels.packages.sales.paymentReceipts])

    const [dataGridColumns, dataGridSortBy, dataGridPageSize, dataGridDensity] = useDataGridSavedState({
        columns: columns,
        name: dataGridSaveConfig.name,
        version: dataGridSaveConfig.version,
    });

    const initialPaginationInfo = useMemo(() => dataGridPageSize ? {
        ...DefaultPaginationInfo,
        pageSize: dataGridPageSize
    } : DefaultPaginationInfo, [dataGridPageSize])

    /**
     * Determines the api request body based on the filters, pagination, and orderBy and needed api callback
     * @type {(function(UseSearchFunctionArg, boolean=): any)}
     */
    const prepareApiRequest = useCallback((args, forExport = false) => {
        const res = {
            cardId: Number(params.id),
            customerId: Number(params.customerId),
            keyword: args.filters?.keyword?.toString(),
            transactionTypeCode: args.filters?.type?.code,
            startDate: args.filters?.startDate,
            endDate: args.filters?.endDate,
            identifier: Number(args.filters?.identifier),
            sorts: []
        }
        if (!forExport) {
            res['paginationDs'] = args.paginationInfo;
        }
        if (args.orderBy?.field === CreditCardTransactionsSummaryDataGridCellKeys.providerSubmitDateTime) {
            args.orderBy.field = CreditCardTransactionsSummaryDataGridCellKeys.submitDateTime;
        }
        if (args.orderBy?.field && orderByPropNames.includes(args.orderBy.field)) {
            res.sorts.push([{
                property: args.orderBy.field,
                isDesc: args.orderBy.descending,
            }])
        }
        return res;
    }, [params.id, params.customerId])

    /**
     * Fetches the transaction summaries for the card based on the filters from the server
     * Sets the [data] in the inner state
     * @type {(function(UseSearchFunctionArg, boolean=): Promise<undefined|ApiResponseDs<*>>)}
     */
    const fetchEntries = useCallback(async (args, forExport = false) => {
        const forApi = prepareApiRequest(args, forExport);
        const response = await Api.searchCustomerCreditCardTransactionHistoryV2(forApi, args.cancelTokenSource?.token);
        if (!isMounted())
            return;
        if (response?.resultFlag && !forExport) {
            setData(response?.data?.items ?? [])
            args.setPaginationInfo(prevState => ({
                ...prevState,
                length: response.data?.totalCount ?? 0,
            }))
        }
        if (response?.resultFlag && forExport)
            return response;
    }, [isMounted, prepareApiRequest])

    /**
     * Searches among the entries based on the filters from the user.
     * @type {UseSearchFunction}
     */
    const search = useCallback(async (result) => {
        await fetchEntries(result, false);
    }, [fetchEntries]);

    const initialSearchDataSettings = useMemo(() => ({
        paginationInfo: initialPaginationInfo,
        orderBy: dataGridSortBy,
    }), [initialPaginationInfo, dataGridSortBy]);

    const searchData = useSearch(search, initialSearchDataSettings)
    const canExportCustomerCreditsSummary = operatorAccessLevels.packages.sales.exportCustomerCreditsSummary;

    /**
     * Handles click on the identifier
     * Sets the identifier in the query or removes it based on the value of the identifier
     * @param {number} identifier
     * @param {boolean} rollback
     */
    const onIdentifierClicked = useCallback((identifier, rollback) => {
        searchData.setFilters(prevState => ({
            ...prevState,
            identifier: rollback ? undefined : identifier,
        }));
    }, [searchData])

    /**
     * Formats the data so that it is acceptable by the csv file generator
     * @param data
     * @return {{title: string, body: (*|*[])}}
     */
    const formatExportingData = useCallback((data) => {
        return {
            title: `Credit Card **** **** **** ${cardInfo?.lastFourDigits ?? ''} transactions`,
            body: data
                ?.map(item => ({
                    'Succeeded': item.succeeded ?? false,
                    'Identifier': item.receiptId ?? '--',
                    'Original Payment': item?.originalPaymentReference ?? '--',
                    'Type': item?.type ?? '--',
                    'Amount': !!item.amount ? formatMoney(`${item.amount}`) : '--',
                    'Auth Code': !!item.authCode ? item.authCode?.toString() : '--',
                    'Submit Date & Time': item.rawSubmittedDateTime ? DateUtils.instance(item.rawSubmittedDateTime).local().format(DateUtils.dateTimeFormat) : '--',
                    'Provider Submit Date & Time': item?.rawSubmittedDateTime ? DateUtils.instance(item.rawSubmittedDateTime).format(DateUtils.dateTimeFormat) + ` (${DateUtils.instance(item.rawSubmittedDateTime).offsetName()})` : '--',
                    'Message': item.terminalMessage ?? '--',
                    'Invoice Reference': item.invoiceReference ?? '--',
                    "Reference No": item?.referenceNumber ?? '--',
                    'ISO': item?.iso?.toString() ?? '--',
                    'Response Code': item?.responseCode ?? '--'
                })) ?? [],
        }
    }, [cardInfo?.lastFourDigits])

    /**
     * Fetches the all transaction items for the credit card
     * Calls the function for generating the csv file
     * @returns {Promise<DataGridExportInfo | undefined>}
     */
    const exportToCsv = useCallback(async () => {
        const res = await fetchEntries(searchData, true);
        if (!isMounted())
            return;
        if (!res?.resultFlag) {
            return;
        }
        return formatExportingData(res?.data?.items ?? []);
    }, [fetchEntries, isMounted, searchData, formatExportingData])


    /**
     * Opens the receipt dialog
     * @type {(function(*): void)|*}
     */
    const openReceiptDialog = useCallback((data) => {
        setReceiptDialogState({
            title: 'Transaction Receipt',
            open: true,
            customerId: defaultCustomer?.id,
            // TODO: receipt id for the print receipt
            receiptId: data.receiptId,
            close: () => setReceiptDialogState(initReceiptDialogState)
        });
    }, [defaultCustomer?.id])


    /**
     * Memo version of rows for the data grid
     */
    const rows = useMemo(() => {
        return data?.map(item => {
            return ({
                key: item.receiptId,
                data: item,
                cells: {
                    [CreditCardTransactionsSummaryDataGridCellKeys.receipt]:
                        <IconButton
                            color={'paper.primary'}
                            onPressed={() => openReceiptDialog(item)}
                        >
                            <ReceiptLongRounded className={'icon normal'}/>
                        </IconButton>,
                    [CreditCardTransactionsSummaryDataGridCellKeys.groupedBy]: {
                        groupValue: item.groupedBy,
                    },
                    [CreditCardTransactionsSummaryDataGridCellKeys.succeeded]: item.succeeded
                        ? <Passed/>
                        : <Failed/>,
                    [CreditCardTransactionsSummaryDataGridCellKeys.identifier]: /*tab === tabs.flatView ?*/
                        Number(searchData.filters?.identifier) === item.receiptId
                            ? <div
                                className={'identifier-roll-back-button'}
                                onClick={() => onIdentifierClicked(item.receiptId, true)}
                            >
                                <RollBackIdentifier/>
                                <p>{item.receiptId}</p>
                            </div>
                            : <div
                                className={'identifier-text-button'}
                                onClick={() => onIdentifierClicked(item.receiptId, false)}
                            >
                                {item.receiptId}
                            </div>,
                    [CreditCardTransactionsSummaryDataGridCellKeys.originalPayment]:
                        Number(searchData.filters?.identifier) === item.originalPaymentReference
                            ? (
                                <div
                                    className={'identifier-roll-back-button'}
                                    onClick={() => onIdentifierClicked(item.originalPaymentReference, true)}
                                >
                                    <RollBackIdentifier/>
                                    <p>{item?.originalPaymentReference ?? "something is wrong"}</p>
                                </div>
                            )
                            : (
                                <div
                                    className={'identifier-text-button'}
                                    onClick={() => onIdentifierClicked(item.originalPaymentReference, false)}
                                >
                                    {item.originalPaymentReference}
                                </div>
                            ),
                    [CreditCardTransactionsSummaryDataGridCellKeys.type]: item?.type,
                    [CreditCardTransactionsSummaryDataGridCellKeys.amount]: item?.amount,
                    [CreditCardTransactionsSummaryDataGridCellKeys.authCode]: item?.authCode,
                    [CreditCardTransactionsSummaryDataGridCellKeys.submitDateTime]: item?.rawSubmittedDateTime ? DateUtils.instance(item.rawSubmittedDateTime).local().format(DateUtils.dateTimeFormat) : null,
                    [CreditCardTransactionsSummaryDataGridCellKeys.providerSubmitDateTime]: item?.rawSubmittedDateTime ? DateUtils.instance(item.rawSubmittedDateTime).format(DateUtils.dateTimeFormat) + ` (${DateUtils.instance(item.rawSubmittedDateTime).offsetName()})` : null,
                    [CreditCardTransactionsSummaryDataGridCellKeys.message]: item?.terminalMessage,
                    [CreditCardTransactionsSummaryDataGridCellKeys.monerisInvoice]: item?.invoiceReference,
                    [CreditCardTransactionsSummaryDataGridCellKeys.reference]: item?.referenceNumber,
                    [CreditCardTransactionsSummaryDataGridCellKeys.iso]: item?.iso,
                    [CreditCardTransactionsSummaryDataGridCellKeys.responseCode]: item?.responseCode,
                }
            })
        })
    }, [data, onIdentifierClicked, openReceiptDialog, searchData.filters?.identifier])

    return (
        <>
            <div className={'new-credit-card-summary-view'}>
                <Row className='no-gutters w-100 d-flex justify-content-between align-items-start mb-3'>
                    <div className={'flex-grow-1'}>
                        <div className={'title-wrapper'}>
                            <IconButton
                                onPressed={() => history.goBack()}
                                color={'paper.secondary'}
                            >
                                <ArrowBack className='icon normal'/>
                            </IconButton>
                            <h2 className={'text-xl font-weight-semi-bold m-0 ml-3'}>
                                Credit Card Usage Summary
                            </h2>
                        </div>
                    </div>
                    <div className={'mx-auto mx-md-2'}>
                        {
                            showCard &&
                            <CreditCard card={cardInfo}/>
                        }
                    </div>
                </Row>
                <CreditCardTransactionSummarySearchSection
                    searchData={searchData}
                    showType
                />
                <div className={'mb-5'}/>
                <PartnerDataGrid
                    ref={dataGridRef}
                    {...dataGridSaveConfig}
                    initialLayoutState={{
                        columns: columns,
                    }}
                    columns={dataGridColumns}
                    rows={rows}
                    singleSelectionOnly
                    state={{
                        sortBy: searchData.orderBy,
                        pagination: searchData.paginationInfo,
                        loading: {state: searchData.loading},
                        density: dataGridDensity
                    }}
                    getExportInfo={canExportCustomerCreditsSummary ? exportToCsv : undefined}
                    onSortByChanged={(_, current) => searchData.setOrderBy(current)}
                    onCurrentPageChanged={(_, current) => searchData.setPaginationInfo(prevState => ({...prevState, ...current}))}
                    onPageSizeChanged={(_, current) => searchData.setPaginationInfo(prevState => ({...prevState, ...current}))}
                />
            </div>
            <TransactionReceiptDialog
                title={receiptDialogState.title}
                open={receiptDialogState.open}
                customerId={receiptDialogState?.customerId}
                receiptId={receiptDialogState?.receiptId}
                setOpen={receiptDialogState?.close}
            />
        </>
    );
}

export default CustomerCreditCardSummaryView;
